import { afterNextRender, Component, Input, signal } from '@angular/core';
import { bootstrapChevronDown } from '@ng-icons/bootstrap-icons';
import { NgIconComponent, provideIcons } from '@ng-icons/core';

/** Breakpoint, ab dem eingeklappt wird – deckungsgleich mit dem SCSS. */
const MOBILE_MQ = '(max-width: 767.98px)';

/**
 * Wrapper für PDP-Abschnitte. Auf dem Desktop immer offen; ist `collapsible`
 * gesetzt, wird der Abschnitt NUR mobil (per CSS-Breakpoint) eingeklappt und
 * ist per Tipp auf die Überschrift aufklappbar.
 *
 * Der Inhalt (inkl. der projizierten <h2 i18n>-Überschrift) bleibt immer im
 * DOM – nur per CSS versteckt –, damit Übersetzungsmarker und die
 * Google-Indexierung (Mobile-First) erhalten bleiben.
 *
 * Nutzung:
 *   <app-pdp-section mbReveal [collapsible]="true">
 *     <h2 class="pdp__h2" heading i18n="@@…">Titel</h2>
 *     …Inhalt…
 *   </app-pdp-section>
 */
@Component({
  selector: 'app-pdp-section',
  standalone: true,
  imports: [NgIconComponent],
  providers: [provideIcons({ bootstrapChevronDown })],
  host: {
    class: 'pdp__section pdps',
    '[class.pdps--collapsible]': 'collapsible',
    '[class.pdps--open]': 'open()',
  },
  template: `
    @if (collapsible) {
      <div
        class="pdps__head"
        [attr.role]="isMobile() ? 'button' : null"
        [attr.tabindex]="isMobile() ? 0 : null"
        [attr.aria-expanded]="isMobile() ? open() : null"
        (click)="toggle()"
        (keydown.enter)="toggle(); $event.preventDefault()"
      >
        <ng-content select="[heading]"></ng-content>
        <ng-icon class="pdps__chev" name="bootstrapChevronDown" aria-hidden="true"></ng-icon>
      </div>
    } @else {
      <div class="pdps__head"><ng-content select="[heading]"></ng-content></div>
    }
    <div class="pdps__body"><ng-content></ng-content></div>
  `,
  styleUrl: './pdp-section.component.scss',
})
export class PdpSectionComponent {
  /** Nur mobil einklappbar; auf dem Desktop immer offen. */
  @Input() collapsible = false;
  /** Aufgeklappt? Gilt nur mobil – Desktop zeigt den Inhalt per CSS immer. */
  readonly open = signal(false);
  /** true nur im mobilen Viewport – nur dann ist der Abschnitt interaktiv/ein Button. */
  readonly isMobile = signal(false);

  constructor() {
    // Interaktivität (Button-Rolle, Toggle) NUR mobil aktivieren. Das Ein-/
    // Ausklappen selbst passiert per CSS-Breakpoint; hier steuern wir nur, dass
    // die Überschrift auf dem Desktop kein Button und nicht klickbar ist.
    afterNextRender(() => {
      const mql = window.matchMedia(MOBILE_MQ);
      this.isMobile.set(mql.matches);
      mql.addEventListener('change', (e) => this.isMobile.set(e.matches));
    });
  }

  toggle(): void {
    if (this.isMobile()) this.open.update((v) => !v);
  }
}
